// ========================================
// FIXED DynamicReportTable.js - Correct globalFilters passing
// ========================================

/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo
} from 'react';
import PropTypes from 'prop-types';
import useApi from 'hooks/useApi';
import {
  getDynamicReportdata,
  getDynamicReportdataForWeather,
  getDropdownFilterData
} from 'api/DynamicReportApi';
import { SERVICES } from 'api/common/constants';
import { debounce } from 'lodash';
import TableComponent from 'screens/common/tanStackTable/Table';
import { getTenantFromToken } from 'utils/routeUtils';
import { downloadExportFile } from 'api/endpoints/Export';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { tableEvents, TABLE_EVENTS } from './TableEventBus';
import MobileReportTable from './MobileReportTable';
import DeliveryDetailsModal from '../tanStackTable/DeliveryDetailsModal';
import DynamicModal from '../DynamicCollapseReport/DynamicModal';
import ViewComponent from './ViewComponent';
import { pulaFarmlandApi } from 'api/endpoints/PulaFarmland';

const DynamicReportTable = ({
  showFilter,
  setExportData,
  onEdit,
  onAdd,
  onCreate,
  onViewDetails,
  apiPath,
  forWeather,
  dropdownFilter,
  dropdownData,
  payloadmode,
  dateFilter,
  district_ids,
  errorMsg,
  responsive,
  cardTittles,
  globalSearch,
  dateRangePicker,
  limit = 25,
  buttons,
  title,
  handleDownload,
  showHeader = true,
  showFooter = true,
  service,
  statusField = 'is_active',
  idField = 'id',
  parentActionHandler,
  exportKey = ''
}) => {
  const tenant = getTenantFromToken();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apiHook = forWeather
    ? getDynamicReportdataForWeather
    : getDynamicReportdata;

  // ===== SINGLE API INSTANCE =====
  const dataAPI = useApi(apiHook);

  // ===== STABLE STATE INITIALIZATION =====
  const [tableData, setTableData] = useState([]);
  const [pageSize, setPageSize] = useState(limit);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [canNextPage, setCanNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ===== STABLE FILTER STATE =====
  const [globalFilters, setGlobalFilters] = useState({
    search_text: '',
    start_date: '',
    end_date: ''
  });

  const [dropdownOptions, setDropdownOptions] = useState({});
  const [selectedValues, setSelectedValues] = useState({});

  // ===== UI STATES =====
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedFarmerId, setSelectedFarmerId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showButtonModal, setShowButtonModal] = useState(false);
  const [selectedModalData, setSelectedModalData] = useState(null);

  // ===== REFS FOR PREVENTING LOOPS =====
  const isRefreshingRef = useRef(false);
  const initialLoadRef = useRef(false);

  // Detect screen size
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobileScreen = windowWidth < 768;

  // ===== SINGLE API FETCH FUNCTION =====
  const fetchData = useCallback(
    async (overrideParams = {}) => {
      if (isRefreshingRef.current) {
        return;
      }

      isRefreshingRef.current = true;
      setIsLoading(true);

      try {
        const currentFilters = overrideParams.filters || globalFilters;
        const currentPageIndex =
          overrideParams.pageIndex !== undefined
            ? overrideParams.pageIndex
            : pageIndex;
        const currentPageSize =
          overrideParams.pageSize !== undefined
            ? overrideParams.pageSize
            : pageSize;

        const requestParams = {
          apiPath,
          cursor: currentPageIndex * currentPageSize,
          limit: currentPageSize,
          tenant,
          filter: {
            ...currentFilters,
            ...Object.fromEntries(searchParams.entries())
          }, // This should contain the actual filter values + URL params
          service
        };

        // Add specific parameters
        if (forWeather && district_ids) {
          requestParams.district_ids = district_ids;
        }

        if (dropdownFilter) {
          requestParams.dropdownOptions = dropdownOptions;
          requestParams.selectedValues = selectedValues;
          requestParams.payloadmode = payloadmode;
        }
        await dataAPI.request(requestParams);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Error fetching data');
      } finally {
        setIsLoading(false);
        isRefreshingRef.current = false;
      }
    },
    [
      apiPath,
      tenant,
      service,
      forWeather,
      district_ids,
      dropdownFilter,
      payloadmode,
      dataAPI,
      globalFilters,
      pageIndex,
      pageSize,
      dropdownOptions,
      selectedValues
    ]
  );

  // ===== PROCESS API RESPONSE =====
  useEffect(() => {
    if (dataAPI.data?.results) {
      const results = dataAPI.data.results;
      setTableData(results.data || []);
      setTotalRows(results.total || 0);
      setCanNextPage(results.remaining_records > 0);

      // Update export data
      const filteredData = results.data?.map(obj => {
        return Object.fromEntries(
          Object.entries(obj).filter(
            ([key]) =>
              !key.startsWith('section') &&
              key !== 'action' &&
              key !== 'id' &&
              key !== 'extra' &&
              key !== 'modalButtons'
          )
        );
      });
      setExportData(filteredData);
    }
  }, [dataAPI.data, setExportData]);

  // ===== DEBOUNCED FETCH =====
  const debouncedFetchData = useMemo(
    () => debounce(params => fetchData(params), 500),
    [fetchData]
  );

  // ===== INITIAL LOAD ONLY ONCE =====
  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      fetchData();
    }
  }, []); // Empty dependency - runs only once

  // ===== DROPDOWN OPTIONS FETCH =====
  useEffect(() => {
    const fetchDropdowns = async () => {
      if (dropdownFilter && Array.isArray(dropdownData)) {
        const allOptions = {};

        for (const item of dropdownData) {
          try {
            const response = await getDropdownFilterData({
              apiPath: item.apiPath,
              service
            });
            allOptions[item.key] = response.data?.results || [];
          } catch (error) {
            console.error('Error fetching dropdown data:', error);
            allOptions[item.key] = [];
          }
        }

        setDropdownOptions(allOptions);
      }
    };

    fetchDropdowns();
  }, [dropdownFilter, dropdownData, service]);

  // ===== EVENT HANDLING =====
  useEffect(() => {
    const unsubscribeOptimistic = tableEvents.on(
      TABLE_EVENTS.STATUS_UPDATE_OPTIMISTIC,
      eventData => {
        const { recordId, rowData, newStatus, statusField, idField } =
          eventData;

        setTableData(prevData =>
          prevData.map(row => {
            const matches = Array.isArray(idField)
              ? idField.every(key => row[key] === rowData[key])
              : row[idField] === recordId;

            return matches ? { ...row, [statusField]: newStatus } : row;
          })
        );
      }
    );

    const unsubscribeSuccess = tableEvents.on(
      TABLE_EVENTS.STATUS_UPDATE_SUCCESS,
      () => {
        setTimeout(() => fetchData(), 300);
      }
    );

    const unsubscribeFailure = tableEvents.on(
      TABLE_EVENTS.STATUS_UPDATE_FAILED,
      () => {
        fetchData();
      }
    );

    return () => {
      unsubscribeOptimistic();
      unsubscribeSuccess();
      unsubscribeFailure();
    };
  }, [fetchData]);

  // ===== STABLE EVENT HANDLERS =====
  const handleGlobalSearch = useCallback(
    searchValue => {
      // Handle both string values and event objects
      const value =
        typeof searchValue === 'string'
          ? searchValue
          : searchValue?.target?.value || '';

      const newFilters = { ...globalFilters, search_text: value };
      setGlobalFilters(newFilters);
      setPageIndex(0);

      // Pass the new filters object directly
      debouncedFetchData({ pageIndex: 0, filters: newFilters });
    },
    [globalFilters, debouncedFetchData]
  );

  // ===== SIMPLE FIX - Replace the handleDateChange function in DynamicReportTable.js =====

  const handleDateChange = useCallback(
    async (date, field) => {
      // Create a new filters object with proper cloning
      const newFilters = { ...globalFilters };

      if (date === null) {
        // Special handling for clearing dates
        newFilters[field] = '';

        // If clearing one date, make sure we're not in an invalid state
        if (field === 'end_date' && newFilters.end_date) {
          // If clearing start date but end date exists, clear both
          newFilters.end_date = '';
        } else if (field === 'start_date' && newFilters.start_date) {
          // If clearing end date but start date exists, keep start date
          // The start date will be validated when selecting a new end date
        }
      } else {
        // Format the date for a new date selection
        const formattedDate = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

        // Update the field with the new date
        newFilters[field] = formattedDate;

        // Validate date range using string comparison (YYYY-MM-DD works for lexicographical comparison)
        if (field === 'end_date' && newFilters.start_date) {
          // If setting end date, validate it's not before start date
          if (formattedDate < newFilters.start_date) {
            newFilters.start_date = '';
            newFilters.end_date = '';
            toast.warning('End date cannot be before start date');
          }
        } else if (field === 'start_date' && newFilters.end_date) {
          // If setting start date, validate it's not after end date
          if (formattedDate > newFilters.end_date) {
            newFilters.end_date = '';
            // We don't necessarily need to clear start date here, just reset end date is enough often,
            // but user might want to keep the new start date.
            toast.warning('Start date cannot be after end date');
          }
        }
      }
      // Update state first
      setGlobalFilters(newFilters);
      setPageIndex(0);

      // Wait a moment for state to settle before triggering API call
      await new Promise(resolve => setTimeout(resolve, 100));

      // Then trigger API call with new filters
      debouncedFetchData({ pageIndex: 0, filters: newFilters });
    },
    [globalFilters, debouncedFetchData]
  );

  const clearAllDates = useCallback(() => {
    const clearedFilters = {
      ...globalFilters,
      start_date: '',
      end_date: ''
    };

    setGlobalFilters(clearedFilters);
    setPageIndex(0);
    debouncedFetchData({ pageIndex: 0, filters: clearedFilters });
  }, [globalFilters, debouncedFetchData]);

  const handlePageChange = useCallback(
    newPageIndex => {
      setPageIndex(newPageIndex);
      fetchData({ pageIndex: newPageIndex });
    },
    [fetchData]
  );

  const handlePageSizeChange = useCallback(
    newPageSize => {
      setPageSize(newPageSize);
      setPageIndex(0);
      fetchData({ pageIndex: 0, pageSize: newPageSize });
    },
    [fetchData]
  );

  // ===== ACTION HANDLERS =====
  const handleAction = useCallback(
    (action, rowData) => {
      if (action === 'Activate' || action === 'Deactivate') {
        if (parentActionHandler) {
          const result = parentActionHandler(action, rowData);
          if (result?.handled) return;
        }
      }

      if (action === 'exportPolygon') {
        const { id, format } = rowData;
        const isProcurement = apiPath === '/procurement/report';
        const exportPromise = isProcurement
          ? pulaFarmlandApi.exportPolygonProcurement(id, format)
          : pulaFarmlandApi.exportPolygon(id, format);

        toast.promise(exportPromise, {
          pending: `Exporting Polygon as ${format.toUpperCase()}...`,
          success: 'Export successful!',
          error: 'Failed to export polygon'
        });
        exportPromise.then(response => {
          const url = window.URL.createObjectURL(new Blob([response]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `farm_polygon_${id}.${format === 'excel' ? 'xlsx' : format}`
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
        });
        return;
      }

      if (action === 'pollEUDRStatus') {
        const pollPromise = pulaFarmlandApi.pollStatus();
        toast.promise(pollPromise, {
          pending: 'Checking EUDR Status...',
          success:
            'Status refresh initiated! Data will update in a few moments.',
          error: 'Failed to initiate status refresh'
        });
        pollPromise.then(() => {
          setTimeout(() => fetchData(), 5000); // Wait a bit for Orbify and DB update
        });
        return;
      }

      if (action === 'edit') {
        onEdit(action, rowData);
      } else if (action === 'add') {
        onAdd(action, rowData);
      } else if (action === 'create') {
        onCreate(action, rowData);
      } else if (action === 'view') {
        setShowViewModal(true);
        setSelectedFarmerId(rowData.id);
        setSelectedRow(rowData);
      } else if (action === 'viewData') {
        setShowButtonModal(true);
        setSelectedModalData({ ...rowData, apiPath });
      } else if (action === 'viewDetails') {
        onViewDetails(action, rowData);
      } else if (action === 'downloadCoords') {
        handleDownloadJSON(action, rowData);
      } else if (action === 'download') {
        handleDownloadAll(rowData);
      } else if (action === 'tupleLink') {
        const { rowData: row, linkData } = rowData;

        let pathStr = `/${tenant}${linkData.path}`;
        if (linkData.queryParam) {
          pathStr += `?${linkData.queryParam}=${row.id}`;
        } else {
          pathStr += `/${row.id}`;
        }
        navigate(pathStr);
      }
    },
    [
      parentActionHandler,
      onEdit,
      onAdd,
      onCreate,
      onViewDetails,
      apiPath,
      navigate,
      tenant
    ]
  );

  const handleDownloadJSON = (action, rowData) => {
    try {
      let coords = rowData.coordinates;
      if (!coords) {
        toast.warn('No coordinates available for download');
        return;
      }

      if (typeof coords === 'string') {
        try {
          coords = JSON.parse(coords);
        } catch (e) {
          console.error('Failed to parse coordinates:', e);
        }
      }

      const id = rowData['Procurement ID'] || rowData.id || 'coordinates';
      const filename = `${id}_polygon.json`;

      const blob = new Blob([JSON.stringify(coords, null, 2)], {
        type: 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading JSON:', error);
      toast.error('Error downloading coordinates');
    }
  };

  const handleDownloadAll = async rowData => {
    try {
      const logId = rowData.id;
      const filenameKey = Object.keys(rowData).find(
        key => key.trim().toLowerCase() === 'filename'
      );
      const filename = rowData[filenameKey]?.trim() || 'export.xlsx';

      const responseData = await downloadExportFile(logId);
      if (!responseData?.success || !responseData.results?.url) {
        throw new Error('Invalid response from downloadExportFile.');
      }

      const response = await fetch(responseData.results.url);
      if (!response.ok) {
        throw new Error(`Failed to fetch file. Status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Error downloading file');
    }
  };

  // ===== STABLE PROPS OBJECT WITH DEBUG =====
  const sharedProps = useMemo(() => {
    const props = {
      // Data props
      tableData,
      totalRows,
      pageSize,
      pageIndex,
      canNextPage,
      isLoading,

      // ===== CRITICAL: Pass actual globalFilters, not empty default =====
      globalfilters: globalFilters, // This should contain the actual filter values
      dropdownOptions,
      selectedValues,
      setSelectedValues,

      // Event handlers
      handleAction,
      handlePageChange,
      handlePageSizeChange,
      handleGlobalSearch,
      handleDateChange,

      // Configuration props
      title: title || '',
      buttons: buttons || [],
      showHeader,
      showFooter,
      handleDownload,
      cardTittles: cardTittles || [],
      errorMsg: errorMsg || '',
      globalSearch,
      dateFilter,
      dropdownFilter,
      dropdownData: dropdownData || [],
      statusField,
      idField,
      parentActionHandler,

      // Export props — passed through to Table for the standalone Export button
      exportKey,
      currentFilters: globalFilters,

      // Custom styles
      customStyles: {
        control: provided => ({
          ...provided,
          minHeight: '80px',
          backgroundColor: 'blue'
        }),
        valueContainer: provided => ({
          ...provided,
          whiteSpace: 'nowrap',
          overflowY: 'auto',
          textOverflow: 'ellipsis',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          maxWidth: '200px',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignContent: 'flex-start',
          maxHeight: '90px'
        }),
        multiValue: provided => ({
          ...provided,
          backgroundColor: 'yellow',
          height: 'fit-content'
        })
      }
    };
    return props;
  }, [
    tableData,
    totalRows,
    pageSize,
    pageIndex,
    canNextPage,
    isLoading,
    globalFilters, // This is the key - make sure it's in dependencies
    dropdownOptions,
    selectedValues,
    setSelectedValues,
    handleAction,
    handlePageChange,
    handlePageSizeChange,
    handleGlobalSearch,
    handleDateChange,
    title,
    buttons,
    showHeader,
    showFooter,
    handleDownload,
    cardTittles,
    errorMsg,
    globalSearch,
    dateFilter,
    dropdownFilter,
    dropdownData,
    statusField,
    idField,
    parentActionHandler,
    exportKey
  ]);

  // ===== RENDER =====
  if (isMobileScreen) {
    return (
      <MobileReportTable
        {...sharedProps}
        filteredTableData={tableData}
        flattenedData={tableData}
        rowCount={totalRows}
        isGlobalSearchActive={!!globalFilters.search_text}
        clearAllDates={clearAllDates}
      />
    );
  }

  return (
    <>
      <TableComponent
        {...sharedProps}
        // Table-specific props
        setExportData={setExportData}
        apiPath={apiPath || ''}
        forWeather={forWeather}
        district_ids={district_ids || []}
        responsive={responsive}
        dateRangePicker={dateRangePicker}
        limit={limit}
        service={service || SERVICES.DIGITIZATION}
        // CRITICAL: Pass centralized data to prevent internal API calls
        centralizedTableData={tableData}
        centralizedTotalRows={totalRows}
        centralizedLoading={isLoading}
        centralizedCanNextPage={canNextPage}
        disableInternalApiCalls={true}
        // CRITICAL: Also pass globalfilters for desktop TableHeader
        globalfilters={globalFilters}
        clearAllDates={clearAllDates}
      />
      <>
        {showViewModal && (
          <ViewComponent
            farmerId={selectedFarmerId}
            rowData={selectedRow}
            onClose={() => setShowViewModal(false)}
          />
        )}
        {(() => {
          if (
            showButtonModal &&
            selectedModalData?.apiPath === '/farmer/sms/log'
          ) {
            return (
              <DeliveryDetailsModal
                show={true}
                onClose={() => setShowButtonModal(false)}
                selectedModalData={selectedModalData}
              />
            );
          } else if (showButtonModal && selectedModalData) {
            return (
              <DynamicModal
                show={true}
                onClose={() => setShowButtonModal(false)}
                selectedModalData={selectedModalData}
              />
            );
          }
          return null;
        })()}
      </>
    </>
  );
};

DynamicReportTable.propTypes = {
  data: PropTypes.array.isRequired,
  deletePorjectAPI: PropTypes.object.isRequired,
  startProcessingAPI: PropTypes.object.isRequired
};

export default DynamicReportTable;
